import { useSession } from "@clerk/clerk-react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Spinner,
} from "@heroui/react";
import { Note } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { CustomerInvoice } from "shared/types/customer-invoice";

interface NotesEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: CustomerInvoice | null;
  onNotesUpdated: (invoice: CustomerInvoice, notes: string) => void;
}

export const NotesEditModal = ({
  isOpen,
  onClose,
  invoice,
  onNotesUpdated,
}: NotesEditModalProps) => {
  const { session } = useSession();
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Determine if we're editing a contact or company
  const isContact = !!invoice?.contact;
  const entityId = isContact ? invoice?.contact?.id : invoice?.company?.id;
  const entityName = invoice?.invoice_partner_display_name;

  // Initialize notes when modal opens
  useEffect(() => {
    if (isOpen && invoice) {
      const currentNotes = isContact
        ? invoice.contact?.notes || ""
        : invoice.company?.notes || "";
      setNotes(currentNotes);
    }
  }, [isOpen, invoice, isContact]);

  const handleSave = async () => {
    if (!entityId || !invoice) return;

    setIsSaving(true);
    try {
      const endpoint = isContact
        ? `${API_URL}/contact/${entityId}/notes`
        : `${API_URL}/contact/company/${entityId}/notes`;

      const response = await fetchWithAuth(
        endpoint,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ notes }),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to update notes");
      }

      toast.success("Notities succesvol opgeslagen");
      onNotesUpdated(invoice, notes);
      onClose();
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Fout bij het opslaan van notities");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <Note weight="duotone" size={24} className="text-moss-primary" />
          <span>
            Notities - {entityName}
            <span className="text-sm font-normal text-neutral-secondary-mid ml-2">
              ({isContact ? "Contact" : "Bedrijf"})
            </span>
          </span>
        </ModalHeader>

        <ModalBody>
          <Textarea
            label="Notities / Opmerkingen"
            placeholder="Voeg hier notities toe over deze klant (bijv. betalingsafspraken, communicatiegeschiedenis, etc.)"
            value={notes}
            onValueChange={setNotes}
            minRows={5}
            maxRows={10}
            classNames={{
              inputWrapper: "border-neutral-secondary-mid/30",
            }}
          />
          <p className="text-xs text-neutral-secondary-mid">
            Deze notities zijn alleen zichtbaar voor medewerkers en worden niet gedeeld met de klant.
          </p>
        </ModalBody>

        <ModalFooter>
          <ButtonNew
            variant="secondaryNeutral"
            label="Annuleren"
            onClick={onClose}
          />
          <ButtonNew
            variant="primaryMoss"
            icon={isSaving ? <Spinner size="sm" /> : undefined}
            label={isSaving ? "Opslaan..." : "Opslaan"}
            onClick={handleSave}
            isDisabled={isSaving}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NotesEditModal;
